import { useState, useEffect } from "react";
import FormField from "components/FormField";
import { isValidDomain } from "pages/RfqNotifications/utils/validation";
import Button from "components/Button";
import Message from "components/Message";
import ContactSearchField from "./ContactSearchField";

import styles from "./BlacklistForm.module.scss";

const BlacklistForm = ({
  entry,
  loading,
  onSave,
  onCancel,
  contacts,
  onContactsSearch
}) => {
  const [formData, setFormData] = useState({
    blacklist_type: "contact",
    contact: null,
    domain: "",
    reason: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (entry) {
      setFormData({
        blacklist_type: entry.blacklist_type || "contact",
        contact: entry.contact || null,
        domain: entry.domain || "",
        reason: entry.reason || ""
      });
    } else {
      // Reset form for create mode
      setFormData({
        blacklist_type: "contact",
        contact: null,
        domain: "",
        reason: ""
      });
      setError("");
      setSuccess("");
      setValidationErrors({});
    }
  }, [entry]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field-specific error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleContactSelect = (contact) => {
    setFormData(prev => ({
      ...prev,
      contact: contact
    }));
    setValidationErrors(prev => ({ ...prev, contact: null }));
  };

  const handleContactClear = () => {
    setFormData(prev => ({
      ...prev,
      contact: null
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (formData.blacklist_type === "contact") {
      if (!formData.contact) {
        errors.contact = "Please select a contact";
      }
    } else if (formData.blacklist_type === "domain") {
      if (!formData.domain.trim()) {
        errors.domain = "Please enter a domain";
      } else {
        // Basic domain validation
        if (!isValidDomain(formData.domain.trim())) {
          errors.domain = "Please enter a valid domain (e.g., example.com)";
        }
      }
    }

    if (formData.reason && formData.reason.length > 500) {
      errors.reason = "Reason must be 500 characters or less";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const entryData = {
        blacklist_type: formData.blacklist_type,
        reason: formData.reason.trim() || null
      };

      if (formData.blacklist_type === "contact") {
        entryData.contact = formData.contact.id;
      } else {
        entryData.domain = formData.domain.trim();
      }

      await onSave(entryData);
      setSuccess(entry ? "Blacklist entry updated successfully!" : "Entry added to blacklist successfully!");
      
      if (!entry) {
        // Reset form after successful creation
        setFormData({
          blacklist_type: "contact",
          contact: null,
          domain: "",
          reason: ""
        });
      }
    } catch (error) {
      setError(error.message || "Failed to save blacklist entry");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.form}>
        <div className={styles.loading}>Loading entry details...</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formHeader}>
        <h2>{entry ? "Edit Blacklist Entry" : "Add to Blacklist"}</h2>
      </div>
      
      {error && <div className={styles.error}>{error}</div>}
      {success && <Message text={success} />}

      <div className={styles.section}>
        <h3>Blacklist Type</h3>
        <div className={styles.radioGroup}>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="blacklist_type"
              value="contact"
              checked={formData.blacklist_type === "contact"}
              onChange={(e) => handleInputChange("blacklist_type", e.target.value)}
            />
            <span className={styles.radioLabel}>
              👤 Specific Contact
            </span>
            <div className={styles.radioDescription}>
              Block a specific customer contact from receiving RFQ notifications
            </div>
          </label>

          <label className={styles.radioOption}>
            <input
              type="radio"
              name="blacklist_type"
              value="domain"
              checked={formData.blacklist_type === "domain"}
              onChange={(e) => handleInputChange("blacklist_type", e.target.value)}
            />
            <span className={styles.radioLabel}>
              🌐 Entire Domain
            </span>
            <div className={styles.radioDescription}>
              Block all email addresses from a specific domain
            </div>
          </label>
        </div>
      </div>

      {/* Contact Selection */}
      {formData.blacklist_type === "contact" && (
        <div className={styles.section}>
          <h3>Contact Selection</h3>
          <ContactSearchField
            contacts={contacts}
            loading={contacts?.loading}
            onSearch={onContactsSearch}
            onSelect={handleContactSelect}
            selectedContact={formData.contact}
            onClear={handleContactClear}
            error={validationErrors.contact}
          />
          {validationErrors.contact && (
            <div className={styles.fieldError}>{validationErrors.contact}</div>
          )}
        </div>
      )}

      {/* Domain Input */}
      {formData.blacklist_type === "domain" && (
        <div className={styles.section}>
          <h3>Domain Information</h3>
          <FormField
            type="text"
            fieldName="domain"
            labelText="Domain Name"
            placeholderText="example.com"
            value={formData.domain}
            onChange={(e) => handleInputChange("domain", e.target.value)}
            error={validationErrors.domain}
            style="form"
          />
          <div className={styles.inputHelp}>
            Enter domain without 'http://' or 'www.' (e.g., example.com)
          </div>
          {validationErrors.domain && (
            <div className={styles.fieldError}>{validationErrors.domain}</div>
          )}
        </div>
      )}

      {/* Reason */}
      <div className={styles.section}>
        <h3>Reason (Optional)</h3>
        <FormField
          type="textarea"
          fieldName="reason"
          labelText="Reason"
          placeholderText="Explain why this contact/domain should be blacklisted..."
          value={formData.reason}
          onChange={(e) => handleInputChange("reason", e.target.value)}
          error={validationErrors.reason}
          style="form"
          className={styles.reasonTextarea}
        />
        <div className={styles.charCount}>
          {formData.reason.length}/500 characters
        </div>
        {validationErrors.reason && (
          <div className={styles.fieldError}>{validationErrors.reason}</div>
        )}
      </div>

      <div className={styles.actions}>
        <div className={styles.leftActions}>
          {/* Empty - keeps buttons on the right */}
        </div>
        <div className={styles.rightActions}>
          <Button type="button" onClick={onCancel} variant="outline">
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Saving..." : (entry ? "Update Entry" : "Add to Blacklist")}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default BlacklistForm;